// src/components/opsdesk/CustomerPicker.tsx
//
// Shared customer search/select for the ops-desk create forms. Hits the
// live /api/customers staff endpoint (debounced); selection is by real
// customer_id — never a typed name.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/components/auth/AuthContext";
import {
  searchCustomers,
  type CustomerSearchRow,
} from "@/lib/opsdesk/api";

export interface CustomerSelection {
  customer_id: number;
  name: string;
}

export function CustomerPicker({
  value,
  onChange,
}: {
  value: CustomerSelection | null;
  onChange: (selection: CustomerSelection | null) => void;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CustomerSearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounce = useRef<number | null>(null);

  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    const q = query.trim();
    if (value || q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (debounce.current !== null) window.clearTimeout(debounce.current);
    setSearching(true);
    debounce.current = window.setTimeout(() => {
      searchCustomers(q)
        .then((r) => {
          setResults(r.rows);
          setOpen(true);
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      if (debounce.current !== null) window.clearTimeout(debounce.current);
    };
  }, [query, value, auth.isReady, auth.isAuthenticated]);

  if (value) {
    return (
      <div className="flex items-center justify-between rounded border border-border bg-bg-surface px-3 py-2 text-sm">
        <span className="text-fg-primary">
          {value.name}{" "}
          <span className="font-mono text-[10px] text-fg-quaternary">
            #{value.customer_id}
          </span>
        </span>
        <button
          type="button"
          aria-label={t("opsdesk.customerPicker.clear")}
          onClick={() => {
            onChange(null);
            setQuery("");
          }}
          className="text-fg-tertiary hover:text-fg-primary"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search
        className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-tertiary"
        strokeWidth={1.5}
      />
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("opsdesk.customerPicker.searchPlaceholder")}
        className="pl-8"
        aria-label={t("opsdesk.customerPicker.searchLabel")}
      />
      {searching && (
        <div className="mt-1 text-[10px] text-fg-quaternary">{t("opsdesk.customerPicker.searching")}</div>
      )}
      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-border bg-bg-panel shadow-lg">
          {results.map((c) => (
            <li key={c.customer_id}>
              <button
                type="button"
                onClick={() => {
                  onChange({ customer_id: c.customer_id, name: c.name });
                  setOpen(false);
                  setResults([]);
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary"
              >
                <span className="truncate">{c.name}</span>
                <span className="shrink-0 font-mono text-[10px] text-fg-quaternary">
                  #{c.customer_id}
                  {c.city ? ` · ${c.city}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !searching && results.length === 0 && query.trim().length >= 2 && (
        <div className="absolute z-20 mt-1 w-full rounded border border-border bg-bg-panel px-3 py-2 text-xs text-fg-tertiary shadow-lg">
          No customer matches “{query.trim()}”.
        </div>
      )}
    </div>
  );
}
